import { useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { api, setStepUpHandler } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SCOPE_LABEL = {
  admin: "this admin change",
  "findpay.refund": "this refund",
  "findpay.merchant": "this merchant change",
  "wallet.withdraw": "this withdrawal",
  "wallet.postpaid": "this billing change",
  "wallet.autoconvert": "this auto-convert change",
  "account.mobile": "this mobile-number change",
  "sessions.logout_others": "signing out your other devices",
};

// Fresh-2FA prompt for critical actions (SEC-STEP-01). Resolves with a grant token, or null on cancel.
export const StepUpDialog = () => {
  const [req, setReq] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const resolver = useRef(null);

  useEffect(() => {
    setStepUpHandler((detail) => new Promise((resolve) => {
      resolver.current = resolve;
      setCode(""); setBackup(false); setError(""); setReq(detail);
    }));
    return () => setStepUpHandler(null);
  }, []);

  const finish = (token) => {
    resolver.current?.(token);
    resolver.current = null;
    setReq(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/2fa/step-up", { challenge_id: req.challenge_id, code: code.trim(), use_backup: backup });
      finish(data.step_up_token);
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (s === 429) setError("Too many attempts — try again in a few minutes.");
      else if (typeof d === "string" && d.includes("challenge")) { setError("This request expired. Please try the action again."); }
      else setError("That code didn't work. Use the newest code from your authenticator app.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) finish(null); }}>
      <DialogContent data-testid="step-up-dialog" className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />Confirm it's you</DialogTitle>
          <DialogDescription data-testid="step-up-scope">
            Enter a fresh {backup ? "backup" : "6-digit authenticator"} code to approve {SCOPE_LABEL[req?.scope] || "this action"}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="step-up-code-input" autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                 inputMode={backup ? "text" : "numeric"} maxLength={backup ? 16 : 6} autoComplete="one-time-code"
                 placeholder={backup ? "Backup code" : "123456"} />
          {error && <p data-testid="step-up-error" className="text-sm text-red-600">{error}</p>}
          <button type="button" data-testid="step-up-toggle-backup" className="text-xs underline text-slate-600"
                  onClick={() => { setBackup(!backup); setCode(""); setError(""); }}>
            {backup ? "Use authenticator code instead" : "Use a backup code"}
          </button>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" data-testid="step-up-cancel" onClick={() => finish(null)}>Cancel</Button>
            <Button type="submit" data-testid="step-up-submit" disabled={busy || !code.trim()}>{busy ? "Checking…" : "Approve"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
